import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import refreshIcon from "../assets/images/refresh.svg";
import flagIcon from "../assets/images/redFlag.png";
import { isDemoMode } from "../demo/demoMode";
import { launchNativeUnitySession } from "../native/mobileBridge";

export function joinClientSession(session, navigate) {
  if (!session) return;

  const userId = localStorage.getItem("PlutonicuserID");

  if (launchNativeUnitySession({
    roomId: session.roomType,
    sessionId: session.meetingID,
    userId,
    displayName: localStorage.getItem("currentNickname"),
    userType: localStorage.getItem("userType") || "Therapist",
    demoBuild: isDemoMode,
  })) {
    return;
  }

  sessionStorage.setItem("meetingID", session.meetingID);
  sessionStorage.setItem("userId", userId);
  navigate(`/unity-room/${session.roomType}`);
}

const TherapistClients = ({
  onViewHistory,
  clientsData,
  fetchClientsData,
  onAvatarSettingChanged,
  loading: externalLoading,
  isAdmin
}) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlocking, setUnlocking] = React.useState(new Set());
  const [savingAvatarSettings, setSavingAvatarSettings] = React.useState(new Set());
  const preferredSpaceCache = {};

  const [meetingsMap, setMeetingsMap] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });


  
  useEffect(() => {

    if (clientsData) {

      processClientsData(clientsData);
    } else if (fetchClientsData && sessions.length === 0 && !externalLoading) {

      fetchClientsData().then((data) => {

        if (data) {
          processClientsData(data);
        }
      });
    } else if (!fetchClientsData && sessions.length === 0) {

      fetchSessions();
    }
  }, [clientsData]);

  const fetchPreferredSpace = async (clientId) => {
  if (isDemoMode) {
    return clientsData?.clients?.find((client) => client.id === clientId)?.preferredSpace || "Not selected";
  }
  // If already fetched once → return cached value
  if (preferredSpaceCache[clientId]) {

    return preferredSpaceCache[clientId];
  }

  try {
    const res = await axiosInstance.get(`/api/get-selected-space/${clientId}`);
    const space = res.data?.selectedSpace || "Not selected";

    // Cache it to avoid duplicate API calls
    preferredSpaceCache[clientId] = space;



    return space;
  } catch (err) {
    console.error("❌ Failed preferredSpace fetch for");
    return "Not selected";
  }
};



  const processClientsData = (data) => {
    try {

      setError(null);

      if (data && data.clients) {

  const allSessions = [];

  const fetchAllPreferredSpaces = data.clients.map(async (client) => {
    const preferredSpace = await fetchPreferredSpace(client.id);

    if (client.sessions && client.sessions.length > 0) {
      client.sessions.forEach((session) => {
        allSessions.push({
          ...session,
          clientName: client.name,
          clientId: client.id,
          clientEmail: client.email,
          clientNickname: client.nickname,
          avatarsEnabled: client.avatarsEnabled !== false,
          preferredSpace,     // ⭐ coming from new endpoint
          userType: client.userType || "Client",
        });
      });
    } else {
      allSessions.push({
        sessionID: null,
        sessionName: null,
        timestamp: null,
        summary: null,
        clientName: client.name,
        clientId: client.id,
        clientEmail: client.email,
        clientNickname: client.nickname,
        avatarsEnabled: client.avatarsEnabled !== false,
        preferredSpace,     // ⭐ correct value even if no sessions
        userType: client.userType || "Client",
        sessionCount: 0,
        isPlaceholder: true,
      });
    }
  });

  // Wait until all preferred space calls finish
  Promise.all(fetchAllPreferredSpaces).then(() => {

    setSessions(allSessions);
    setLoading(false);
  });

  return; // prevent the old code from running below
}
 else if (data && data.summaries) {

        const processedSessions = processSessionSummaries(data.summaries);
        setSessions(processedSessions);
      } else {

        setSessions([]);
      }
    } catch (err) {
      console.error("❌ DEBUG: Error processing clients data:");
      setError("Failed to process session data");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {

      setLoading(true);
      setError(null);

      const response = await axiosInstance.get("/api/summarize");


      if (response.data) {

        if (response.data.clients) {

          const allSessions = [];
          response.data.clients.forEach((client, idx) => {

            if (client.sessions && client.sessions.length > 0) {
              client.sessions.forEach((session, sIdx) => {

                allSessions.push({
                  ...session,
                  clientName: client.name,
                  clientId: client.id,
                  clientEmail: client.email,
                  clientNickname: client.nickname,
                  avatarsEnabled: client.avatarsEnabled !== false,
                  preferredSpace: client.preferredSpace,
                  userType: client.userType || "Client",
                });
              });
            } else {

              allSessions.push({
                sessionID: null,
                sessionName: null,
                timestamp: null,
                summary: null,
                clientName: client.name,
                clientId: client.id,
                clientEmail: client.email,
                clientNickname: client.nickname,
                avatarsEnabled: client.avatarsEnabled !== false,
                preferredSpace: client.preferredSpace || "No sessions yet",
                userType: client.userType || "Client",
                sessionCount: 0,
                isPlaceholder: true,
              });
            }
          });


          setSessions(allSessions);
        } else if (response.data.summaries) {

          const processedSessions = processSessionSummaries(response.data.summaries);
          setSessions(processedSessions);
        } else {

          setSessions([]);
        }
      } else {

        setSessions([]);
      }
    } catch (err) {
      console.error("❌ DEBUG: Error fetching sessions:");
      setError(err.response?.data?.message || "Failed to fetch sessions");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const processSessionSummaries = (summaries) => {

    const processedSessions = summaries.map((summary, idx) => {
      const participantMatch = summary.summary.match(/\*\*Participants?:\*\*\s*([^\n]+)/);
      const participant = participantMatch ? participantMatch[1].trim() : "Unknown Client";
      const clientId = participant.toLowerCase().replace(/\s+/g, "-");


      return {
        sessionID: summary.sessionID,
        sessionName: summary.sessionName,
        timestamp: summary.timestamp,
        summary: summary.summary,
        clientName: participant,
        clientId: clientId,
        clientNickname: participant,
        preferredSpace: summary.sessionName || "No preference",
        userType: "Client",
      };
    });


    return processedSessions.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  };
  const fetchSessionByName = async (sessionName) => {
  try {
    const response = await axiosInstance.get(
      `/items/by-sessionname/${encodeURIComponent(sessionName)}`
    );
    return response.data;
  } catch (err) {
    console.error("❌ Failed to fetch session by name:");
    return null;
  }
};

useEffect(() => {
  if (isDemoMode) return undefined;
  let interval;

  const fetchMeetings = async () => {
    try {
      const userId = localStorage.getItem("PlutonicuserID");

      const res = await axiosInstance.get(`/api/GetMeetings/${userId}`);

      const map = {};

      res.data.forEach((room) => {
        map[room.meetingID] = {
          meetingID: room.meetingID,
          userIds: room.PlutonicuserID,
          onlineArray: room.online,
          sessionTime: room.sessionTime,
        };
      });

      setMeetingsMap(map);
    } catch (err) {
      console.error("❌ Failed to fetch meetings");
    }
  };

  fetchMeetings();
  interval = setInterval(fetchMeetings, 5000);

  return () => clearInterval(interval);
}, []);

const extractRoomId = (name = "") => {
  const n = (name || "").toLowerCase();
  if (n.includes("beach")) return "beach";
  if (n.includes("forest")) return "mountain";
  if (n.includes("park")) return "park";
  if (n.includes("camp")) return "campsite";
  if (n.includes("winter")) return "winter-holiday";
  return null;
};

const getClientOnlineSession = (session) => {
  if (!session?.clientId) return null;

  for (const meeting of Object.values(meetingsMap)) {
    const index = meeting.userIds?.findIndex(
      (id) => id === session.clientId
    );

    if (index >= 0 && meeting.onlineArray?.[index]) {
      const roomType = meeting.meetingID
        .split(".")[0]
        .replace("-therapy", "");

      return {
        meetingID: meeting.meetingID,
        roomType,
      };
    }
  }

  return null;
};

const getClientNextSession = (session) => {
  if (!session?.clientId) return { status: "none" };

  const now = Date.now();
  const availableBeforeMs = 15 * 60 * 1000;
  const availableAfterMs = 75 * 60 * 1000;
  const uniqueSessions = new Map();

  Object.values(meetingsMap).forEach((meeting) => {
    if (!meeting.userIds?.includes(session.clientId) || !meeting.sessionTime) return;
    const start = new Date(meeting.sessionTime);
    if (Number.isNaN(start.getTime())) return;
    const sessionKey = meeting.meetingID?.split(".")[1] || start.toISOString();
    if (!uniqueSessions.has(sessionKey)) uniqueSessions.set(sessionKey, start);
  });

  const scheduled = Array.from(uniqueSessions.values()).sort((a, b) => a - b);
  const active = scheduled.find((start) => {
    const delta = start.getTime() - now;
    return delta <= availableBeforeMs && delta >= -availableAfterMs;
  });
  if (active) return { status: "active", start: active };
  const upcoming = scheduled.find((start) => start.getTime() > now);
  return upcoming ? { status: "upcoming", start: upcoming } : { status: "none" };
};

const formatSessionTime = (date) => date.toLocaleString("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const handleSort = (key) => {
  setSortConfig((current) => ({
    key,
    direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
  }));
};

const sortedSessions = [...sessions].sort((a, b) => {
  if (sortConfig.key === "name") {
    const comparison = (a.clientName || "").localeCompare(b.clientName || "", undefined, { sensitivity: "base" });
    return sortConfig.direction === "asc" ? comparison : -comparison;
  }
  const aTime = getClientNextSession(a).start?.getTime();
  const bTime = getClientNextSession(b).start?.getTime();
  if (aTime === undefined && bTime === undefined) return (a.clientName || "").localeCompare(b.clientName || "");
  if (aTime === undefined) return 1;
  if (bTime === undefined) return -1;
  return sortConfig.direction === "asc" ? aTime - bTime : bTime - aTime;
});

const sortIndicator = (key) => {
  if (sortConfig.key !== key) return "↕";
  return sortConfig.direction === "asc" ? "↑" : "↓";
};

const handleJoin = (session) => {
  joinClientSession(session, navigate);
};

async function handleUnlock(session) {
  const { sessionID } = session;

  // Optimistic UI: mark as unlocking + clear the lock locally
  setUnlocking(prev => new Set(prev).add(sessionID));
  setSessions(prev =>
    prev.map(s =>
      s.sessionID === sessionID ? { ...s, locked: false } : s
    )
  );

  try {
    // 🔧 Call your Flask route
    const res = await axiosInstance.post(`/api/set-locked-status/${sessionID}`, {
      locked: false, // explicitly unlock
    });

    if (res.status < 200 || res.status >= 300) {
      throw new Error(`HTTP ${res.status}`);
    }


  } catch (err) {
    // Revert on error
    console.error("❌ Unlock error:");
    setSessions(prev =>
      prev.map(s =>
        s.sessionID === sessionID ? { ...s, locked: true } : s
      )
    );
    alert('Failed to unlock. Please try again.');
  } finally {
    setUnlocking(prev => {
      const next = new Set(prev);
      next.delete(sessionID);
      return next;
    });
  }
}

async function handleAvatarSettingChange(clientId, enabled) {
  if (!clientId || savingAvatarSettings.has(clientId)) return;
  const previous = sessions.find((session) => session.clientId === clientId)?.avatarsEnabled !== false;
  setSavingAvatarSettings((current) => new Set(current).add(clientId));
  setSessions((current) => current.map((session) =>
    session.clientId === clientId ? { ...session, avatarsEnabled: enabled } : session
  ));
  try {
    if (!isDemoMode) {
      await axiosInstance.put(`/api/clients/${encodeURIComponent(clientId)}/avatar-setting`, {
        avatarsEnabled: enabled,
      });
    }
    onAvatarSettingChanged?.(clientId, enabled);
  } catch (err) {
    setSessions((current) => current.map((session) =>
      session.clientId === clientId ? { ...session, avatarsEnabled: previous } : session
    ));
    alert("Avatar setting could not be saved. Please try again.");
  } finally {
    setSavingAvatarSettings((current) => {
      const next = new Set(current);
      next.delete(clientId);
      return next;
    });
  }
}



  const handleViewHistory = (session) => {

    const sessionID = session.sessionID;
    if (sessionID) {
      navigate(`/clients/${sessionID}/overview`);
    } else if (session.isPlaceholder) {
      alert(`${session.clientName} has no AI companion sessions yet.`);
    } else {
      if (onViewHistory) {
        const clientWithSession = {
          id: session.clientId,
          name: session.clientName,
          nickname: session.clientNickname,
          preferredSpace: session.preferredSpace,
          userType: session.userType || "Client",
          sessions: [session],
        };

        onViewHistory(clientWithSession);
      }
    }
  };

  if (loading || externalLoading) {
    return (
      <div className="bg-white rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-blue-600">Clients</h3>
        </div>
        <div className="px-6 py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading therapy sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-blue-600">Clients</h3>
        </div>
        <div className="px-6 py-12 text-center">
          <div className="text-red-500 mb-2">
            <svg
              className="w-8 h-8 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <p className="text-red-600 font-medium">Error loading sessions</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
          <button
            onClick={fetchSessions}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Compute which clients have locked sessions
  const lockedClients = new Set(
    sessions
      .filter((s) => s.locked === true)
      .map((s) => s.clientName)
  );
  return (
    <div className="bg-white rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-blue-600">Clients</h3>
        <button
          onClick={() => {
            if (fetchClientsData) {
              fetchClientsData(true).then((data) => {
                if (data) {
                  processClientsData(data);
                }
              });
            } else {
              fetchSessions();
            }
          }}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          <img src={refreshIcon} alt="Refresh" className="w-8 h-8" />
        </button>
      </div>

      <div className="md:hidden p-3 space-y-3">
        {sortedSessions.map((session, index) => {
          const onlineSession = getClientOnlineSession(session);
          return (
            <article key={session.sessionID || index} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {lockedClients.has(session.clientName) && <img src={flagIcon} alt="Locked session" className="w-4 h-4" />}
                    <h4 className="font-bold text-base truncate">{session.clientName || "Unknown Client"}</h4>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{session.clientNickname || "Unknown nickname"}</p>
                </div>
                {onlineSession ? (
                  <button onClick={() => handleJoin(onlineSession)} className="shrink-0 px-3 py-1.5 bg-blue-600 text-white rounded-full text-xs font-semibold">Join</button>
                ) : <span className="shrink-0 text-xs text-gray-400">Offline</span>}
              </div>
              <dl className="mt-4 grid grid-cols-[auto,1fr] gap-x-3 gap-y-2 text-sm">
                <dt className="text-gray-500">Preferred space</dt>
                <dd className="text-right font-medium">{session.preferredSpace || "Unknown"}</dd>
                <dt className="text-gray-500">Next session</dt>
                <dd className="text-right font-medium">
                  {(() => {
                    const nextSession = getClientNextSession(session);
                    if (nextSession.status === "active") return "Available now";
                    if (nextSession.status === "upcoming") return formatSessionTime(nextSession.start);
                    return "Not scheduled";
                  })()}
                </dd>
                <dt className="text-gray-500">Avatars</dt>
                <dd className="text-right font-medium">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-blue-600"
                      checked={session.avatarsEnabled !== false}
                      disabled={savingAvatarSettings.has(session.clientId)}
                      onChange={(event) => handleAvatarSettingChange(session.clientId, event.target.checked)}
                      aria-label={`Avatars for ${session.clientName || "client"}`}
                    />
                    <span>{session.avatarsEnabled !== false ? "On" : "Off"}</span>
                  </label>
                </dd>
              </dl>
              <button
                onClick={() => navigate("/clients/schedule", { state: { clientId: session.clientId, clientEmail: session.clientEmail, clientName: session.clientName } })}
                className="mt-4 w-full min-h-11 rounded-full bg-blue-600 text-white text-sm font-semibold"
              >
                Schedule Session
              </button>
            </article>
          );
        })}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-200">
            <tr>
              <th
                className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider"
                aria-sort={sortConfig.key === "name" ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
              >
                <button type="button" onClick={() => handleSort("name")} className="inline-flex items-center gap-1 hover:text-blue-700">
                  Name <span aria-hidden="true">{sortIndicator("name")}</span>
                </button>
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                Nickname
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                Preferred Space
              </th>
              <th
                className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider"
                aria-sort={sortConfig.key === "nextSession" ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
              >
                <button type="button" onClick={() => handleSort("nextSession")} className="inline-flex items-center gap-1 hover:text-blue-700">
                  Next Session <span aria-hidden="true">{sortIndicator("nextSession")}</span>
                </button>
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold">
                Online
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                Worksheets
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                Avatars
              </th>

              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedSessions.map((session, index) => (
              <tr key={session.sessionID || index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center gap-2">
                    {lockedClients.has(session.clientName) && (
                      <img
                        src={flagIcon}
                        alt="Locked session"
                        title="Client has locked sessions"
                        className="w-4 h-4"
                      />
                    )}
                    <span className={lockedClients.has(session.clientName) ? "pl-1" : ""}>
                      {session.clientName || "Unknown Client"}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {session.clientNickname || "Unknown"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {session.preferredSpace || "Unknown Space"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {(() => {
                    const nextSession = getClientNextSession(session);
                    if (nextSession.status === "active") {
                      return (
                        <div>
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 font-medium text-green-700">Available now</span>
                          <div className="mt-1 text-xs text-gray-500">{formatSessionTime(nextSession.start)}</div>
                        </div>
                      );
                    }
                    if (nextSession.status === "upcoming") return <span className="text-gray-700">{formatSessionTime(nextSession.start)}</span>;
                    return <span className="text-gray-400">Not scheduled</span>;
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {(() => {
                    const onlineSession = getClientOnlineSession(session);

                    if (!onlineSession) {
                      return <span className="text-gray-400">—</span>;
                    }

                    return (
                      <button
                        onClick={() => handleJoin(onlineSession)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Join
                      </button>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => {}}
                    className="text-gray-400 font-medium cursor-not-allowed"
                    disabled
                  >
                    View
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <label className="inline-flex items-center gap-2 font-medium">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-blue-600"
                      checked={session.avatarsEnabled !== false}
                      disabled={savingAvatarSettings.has(session.clientId)}
                      onChange={(event) => handleAvatarSettingChange(session.clientId, event.target.checked)}
                      aria-label={`Avatars for ${session.clientName || "client"}`}
                    />
                    <span>{session.avatarsEnabled !== false ? "On" : "Off"}</span>
                  </label>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
  <button
    onClick={() => {

      navigate("/clients/schedule", {
        state: {
          clientId: session.clientId,
          clientEmail: session.clientEmail,
          clientName: session.clientName,
        }
      });
    }}
    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition"
  >
    Schedule Session
  </button>
</td>


              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sessions.length === 0 && !loading && !error && (
        <div className="px-6 py-12 text-center">
          <p className="text-gray-500">No therapy sessions found.</p>
          <p className="text-gray-400 text-sm mt-1">
            Therapy session summaries will appear here once clients have
            sessions.
          </p>
        </div>
      )}
    </div>
  );
};

export default TherapistClients;
